import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const SERVICES_SCHEMA = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebPage",
      "@id": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/services#webpage`,
      "url": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/services`,
      "name": "Electrical Services NYC & Westchester – DTPU | Done to Power Up",
      "description": "Comprehensive residential and commercial electrical services in NYC. Panel upgrades, EV charger installation, smart home wiring, emergency electrical service, warehouse lighting, conduit systems and more.",
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": import.meta.env.VITE_SITE_URL || "https://example.com" },
          { "@type": "ListItem", "position": 2, "name": "Services", "item": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/services` }
        ]
      }
    },
    {
      "@type": "ItemList",
      "name": "Electrical Services Offered by DTPU",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "item": { "@type": "Service", "name": "Electrical Repairs & Troubleshooting", "provider": { "@type": "ElectricalContractor", "name": "Done to Power Up" } } },
        { "@type": "ListItem", "position": 2, "item": { "@type": "Service", "name": "Panel Upgrades & Service Upgrades", "provider": { "@type": "ElectricalContractor", "name": "Done to Power Up" } } },
        { "@type": "ListItem", "position": 3, "item": { "@type": "Service", "name": "Lighting Installation Indoor & Outdoor", "provider": { "@type": "ElectricalContractor", "name": "Done to Power Up" } } },
        { "@type": "ListItem", "position": 4, "item": { "@type": "Service", "name": "Smart Home Wiring & Automation", "provider": { "@type": "ElectricalContractor", "name": "Done to Power Up" } } },
        { "@type": "ListItem", "position": 5, "item": { "@type": "Service", "name": "EV Charger Installation", "provider": { "@type": "ElectricalContractor", "name": "Done to Power Up" } } },
        { "@type": "ListItem", "position": 6, "item": { "@type": "Service", "name": "Rewiring & Renovations", "provider": { "@type": "ElectricalContractor", "name": "Done to Power Up" } } },
        { "@type": "ListItem", "position": 7, "item": { "@type": "Service", "name": "Surge Protection Systems", "provider": { "@type": "ElectricalContractor", "name": "Done to Power Up" } } },
        { "@type": "ListItem", "position": 8, "item": { "@type": "Service", "name": "Emergency Electrical Service 24/7", "provider": { "@type": "ElectricalContractor", "name": "Done to Power Up" } } },
        { "@type": "ListItem", "position": 9, "item": { "@type": "Service", "name": "Tenant Fit-Outs & Commercial Installations", "provider": { "@type": "ElectricalContractor", "name": "Done to Power Up" } } },
        { "@type": "ListItem", "position": 10, "item": { "@type": "Service", "name": "Warehouse & Industrial Lighting", "provider": { "@type": "ElectricalContractor", "name": "Done to Power Up" } } },
        { "@type": "ListItem", "position": 11, "item": { "@type": "Service", "name": "Piping & EMT Conduit Systems", "provider": { "@type": "ElectricalContractor", "name": "Done to Power Up" } } },
        { "@type": "ListItem", "position": 12, "item": { "@type": "Service", "name": "Three-Phase Power & Equipment Hookups", "provider": { "@type": "ElectricalContractor", "name": "Done to Power Up" } } },
        { "@type": "ListItem", "position": 13, "item": { "@type": "Service", "name": "Demo & Retrofit Services", "provider": { "@type": "ElectricalContractor", "name": "Done to Power Up" } } },
        { "@type": "ListItem", "position": 14, "item": { "@type": "Service", "name": "Code Compliance & Corrections", "provider": { "@type": "ElectricalContractor", "name": "Done to Power Up" } } },
        { "@type": "ListItem", "position": 15, "item": { "@type": "Service", "name": "Rooftop Equipment Electrical", "provider": { "@type": "ElectricalContractor", "name": "Done to Power Up" } } }
      ]
    }
  ]
};

const ServicesPage = () => {
  useEffect(() => {
    // Handle anchor link scrolling
    const hash = window.location.hash;
    if (hash) {
      const element = document.querySelector(hash);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-services';
    script.textContent = JSON.stringify(SERVICES_SCHEMA);
    document.head.appendChild(script);
    document.title = 'Electrical Services NYC & Westchester – DTPU | Done to Power Up';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'Comprehensive residential and commercial electrical services in NYC & Westchester. Panel upgrades, EV charger installation, smart home wiring, 24/7 emergency electrical service and more.');
    return () => { document.getElementById('schema-services')?.remove(); };
  }, []);

  const services = [
    {
      title: 'Electrical Repairs & Troubleshooting',
      description: 'Expert diagnosis and repair of all electrical issues including flickering lights, tripped breakers, faulty outlets, and wiring problems. Our licensed electricians use advanced diagnostic tools to identify and fix problems quickly and safely.',
      icon: 'ri-tools-line',
      features: [
        'Circuit breaker troubleshooting',
        'Outlet and switch repair',
        'Wiring fault detection',
        'Electrical safety inspections',
      ],
      category: 'residential',
      iconLink: '/contact',
    },
    {
      title: 'Panel Upgrades & Service Upgrades',
      description: 'Upgrade outdated electrical panels and service capacity from 100A to 400A+ to handle modern power demands. We install new circuit breakers, replace old fuse boxes, and ensure your electrical system meets current safety codes.',
      icon: 'ri-dashboard-line',
      features: [
        '100A to 400A+ service upgrades',
        'Panel upgrades and replacement',
        'Circuit breaker replacement',
        'Subpanel installation',
      ],
      category: 'residential',
      iconLink: '/contact',
    },
    {
      title: 'Lighting Installation (Indoor/Outdoor)',
      description: 'Professional lighting design and installation for residential and commercial properties. From recessed lighting to landscape illumination, we create the perfect ambiance while maximizing energy efficiency.',
      icon: 'ri-lightbulb-line',
      features: [
        'Recessed lighting installation',
        'Chandelier and fixture mounting',
        'Landscape and security lighting',
        'LED retrofit and upgrades',
      ],
      category: 'residential',
      iconLink: '/contact',
    },
    {
      title: 'Smart Home Wiring & Automation',
      description: 'Transform your home with intelligent electrical systems. We install and configure smart switches, automated lighting, security systems, and integrated home automation platforms.',
      icon: 'ri-home-wifi-line',
      features: [
        'Smart switch installation',
        'Home automation setup',
        'Security system wiring',
        'Voice control integration',
      ],
      category: 'residential',
      iconLink: '/contact',
    },
    {
      title: 'EV Charger Installation',
      description: 'Professional installation of Level 2 electric vehicle charging stations for home and business. We handle permits, electrical upgrades, and ensure optimal charging performance.',
      icon: 'ri-charging-pile-2-line',
      features: [
        'Level 2 charger installation',
        'Electrical capacity assessment',
        'Permit acquisition',
        'Multiple charging stations',
      ],
      category: 'residential',
      iconLink: '/contact',
    },
    {
      title: 'Rewiring & Renovations',
      description: 'Complete rewiring services for older homes and renovation projects. We replace outdated wiring systems, upgrade to modern standards, and ensure safe, code-compliant electrical installations throughout your property.',
      icon: 'ri-hammer-line',
      features: [
        'Complete home rewiring',
        'Renovation electrical work',
        'Old wiring replacement',
        'Code compliance upgrades',
      ],
      category: 'residential',
      iconLink: '/contact',
    },
    {
      title: 'Surge Protection Systems',
      description: 'Protect your valuable electronics and appliances from power surges and lightning strikes. We install whole-house surge protection and point-of-use surge suppressors.',
      icon: 'ri-shield-flash-line',
      features: [
        'Whole-house surge protection',
        'Point-of-use suppressors',
        'Lightning protection systems',
        'Equipment warranty protection',
      ],
      category: 'residential',
      iconLink: '/contact',
    },
    {
      title: 'Emergency Electrical Service',
      description: 'Available 24/7 for urgent electrical emergencies. Power outages, sparking outlets, burning smells, and other safety hazards are addressed immediately by our emergency response team.',
      icon: 'ri-alarm-warning-line',
      features: [
        '24/7 emergency response',
        'Power restoration',
        'Safety hazard resolution',
        'Same-day service',
      ],
      highlight: true,
      category: 'commercial',
      iconLink: '/contact',
    },
    {
      title: 'Tenant Fit-Outs & Commercial Installations',
      description: 'Complete electrical solutions for commercial tenant improvements and new installations. We handle office build-outs, retail spaces, and commercial renovations with minimal disruption to your business operations.',
      icon: 'ri-building-line',
      features: [
        'Office tenant fit-outs',
        'Retail space electrical',
        'Commercial renovations',
        'New construction wiring',
      ],
      category: 'commercial',
      iconLink: '/contact',
    },
    {
      title: 'Warehouse & Industrial Lighting',
      description: 'High-efficiency lighting solutions for warehouses, factories, and industrial facilities. We design and install LED high-bay lighting, motion sensors, and energy-efficient systems that reduce costs and improve visibility.',
      icon: 'ri-flashlight-line',
      features: [
        'LED high-bay lighting',
        'Motion sensor systems',
        'Energy-efficient upgrades',
        'Industrial lighting design',
      ],
      category: 'commercial',
      iconLink: '/contact',
    },
    {
      title: 'Piping & EMT Conduit Systems',
      description: 'Professional installation of electrical conduit systems including EMT, rigid, and flexible conduit. We ensure proper cable protection, code compliance, and clean, organized electrical runs for commercial and industrial projects.',
      icon: 'ri-stack-line',
      features: [
        'EMT conduit installation',
        'Rigid conduit systems',
        'Cable tray installation',
        'Underground conduit runs',
      ],
      category: 'commercial',
      iconLink: '/contact',
    },
    {
      title: 'Three-Phase Power & Equipment Hookups',
      description: 'Installation and connection of three-phase power systems for commercial and industrial equipment. We handle machinery hookups, motor connections, and heavy equipment electrical requirements.',
      icon: 'ri-plug-line',
      features: [
        'Three-phase power installation',
        'Machinery electrical hookups',
        'Motor connections',
        'Heavy equipment wiring',
      ],
      category: 'commercial',
      iconLink: '/contact',
    },
    {
      title: 'Demo & Retrofit Services',
      description: 'Safe demolition of old electrical systems and retrofit installations for modernization projects. We remove outdated equipment, upgrade to current standards, and ensure seamless integration with existing infrastructure.',
      icon: 'ri-delete-bin-line',
      features: [
        'Electrical system demolition',
        'Equipment removal',
        'Retrofit installations',
        'System modernization',
      ],
      category: 'commercial',
      iconLink: '/contact',
    },
    {
      title: 'Code Compliance & Corrections',
      description: 'Comprehensive electrical code compliance services including inspections, violations corrections, and upgrades to meet current NEC standards. We work with inspectors to ensure your property passes all electrical inspections.',
      icon: 'ri-file-list-3-line',
      features: [
        'Code violation corrections',
        'NEC compliance upgrades',
        'Inspection preparation',
        'Permit coordination',
      ],
      category: 'commercial',
      iconLink: '/contact',
    },
    {
      title: 'Rooftop Equipment Electrical',
      description: 'Specialized electrical installations for rooftop equipment including HVAC units, exhaust fans, solar panels, and telecommunications equipment. We handle weatherproof connections, disconnects, and proper grounding.',
      icon: 'ri-building-2-line',
      features: [
        'HVAC electrical connections',
        'Rooftop equipment hookups',
        'Weatherproof installations',
        'Disconnect and grounding',
      ],
      category: 'commercial',
      iconLink: '/contact',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-secondary via-secondary to-gray-800">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')]"></div>
        </div>
        
        <div className="relative max-w-[1280px] mx-auto px-6 lg:px-12 text-center">
          <p className="text-primary text-xs font-bold tracking-[0.2em] uppercase mb-4">
            PROFESSIONAL ELECTRICAL SERVICES
          </p>
          <h1 className="text-6xl font-bold text-white mb-6">
            Our Services
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            From routine maintenance to complex installations, DTPU provides comprehensive electrical solutions for residential and commercial properties throughout NYC. All work is performed by licensed electricians and backed by our satisfaction guarantee.
          </p>
        </div>
      </section>

      {/* Residential Services */}
      <section id="residential-services" className="py-20 bg-white scroll-mt-20">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <p className="text-primary text-xs font-bold tracking-[0.2em] uppercase mb-3">
              RESIDENTIAL SERVICES
            </p>
            <h2 className="text-4xl font-bold text-secondary mb-4">
              Home Electrical Solutions
            </h2>
            <p className="text-gray-600 text-base max-w-2xl mx-auto">
              Professional electrical services for homeowners including upgrades, installations, and repairs.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {services.filter(s => s.category === 'residential').map((service, index) => (
              <Link
                key={index}
                to="/contact"
                className={`block bg-white border-2 ${
                  service.highlight ? 'border-red-500' : 'border-gray-200'
                } rounded-2xl p-8 hover:shadow-xl transition-all duration-300 cursor-pointer`}
              >
                <div className="flex items-start justify-between mb-6">
                  <Link
                    to={service.iconLink}
                    className="w-14 h-14 flex items-center justify-center bg-primary/10 rounded-xl hover:bg-primary/20 transition-colors duration-300 cursor-pointer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <i className={`${service.icon} text-primary text-3xl`}></i>
                  </Link>
                  {service.highlight && (
                    <span className="bg-red-500 text-white text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">
                      24/7 AVAILABLE
                    </span>
                  )}
                </div>

                <h3 className="text-2xl font-bold text-secondary mb-4">
                  {service.title}
                </h3>

                <p className="text-gray-600 text-sm leading-relaxed mb-6">
                  {service.description}
                </p>

                <div className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center space-x-2">
                      <i className="ri-check-line text-primary text-lg"></i>
                      <span className="text-sm text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Commercial Services */}
      <section id="commercial-services" className="py-20 bg-gray-50 scroll-mt-20">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <p className="text-primary text-xs font-bold tracking-[0.2em] uppercase mb-3">
              COMMERCIAL SERVICES
            </p>
            <h2 className="text-4xl font-bold text-secondary mb-4">
              Commercial & Industrial Solutions
            </h2>
            <p className="text-gray-600 text-base max-w-2xl mx-auto">
              Comprehensive electrical contracting for businesses, warehouses, and industrial facilities.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {services.filter(s => s.category === 'commercial').map((service, index) => (
              <Link
                key={index}
                to="/contact"
                className="block bg-white border-2 border-gray-200 rounded-2xl p-8 hover:shadow-xl transition-all duration-300 cursor-pointer"
              >
                <div className="flex items-start justify-between mb-6">
                  <Link
                    to={service.iconLink}
                    className="w-14 h-14 flex items-center justify-center bg-primary/10 rounded-xl hover:bg-primary/20 transition-colors duration-300 cursor-pointer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <i className={`${service.icon} text-primary text-3xl`}></i>
                  </Link>
                </div>

                <h3 className="text-2xl font-bold text-secondary mb-4">
                  {service.title}
                </h3>

                <p className="text-gray-600 text-sm leading-relaxed mb-6">
                  {service.description}
                </p>

                <div className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center space-x-2">
                      <i className="ri-check-line text-primary text-lg"></i>
                      <span className="text-sm text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary to-blue-700">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Contact us today for a free consultation and quote. Our team is ready to help with all your electrical needs.
          </p>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            Need immediate assistance? Our emergency team is standing by 24/7.
          </p>
          <a
            href="tel:+19145016880"
            className="inline-flex items-center justify-center bg-red-600 text-white px-10 py-5 rounded-full font-bold text-lg hover:bg-red-700 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
          >
            <i className="ri-phone-line mr-3 text-2xl"></i>
            Call Emergency Line: (914) 501-6880
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServicesPage;
